import React, { useState } from 'react';
import { useOnboarding } from './OnboardingContext';

export function FeatureLevelStep({ stepId }) {
  const { nextStep, prevStep, markStepComplete, setProfileDraft } = useOnboarding();
  const [selectedTier, setSelectedTier] = useState('');

  const tiers = [
    {
      id: 'beta',
      name: 'Beta',
      price: 'Free',
      description: 'Perfect for trying out the platform',
      features: [
        'Basic workout tracking',
        'Limited exercise library',
        'Weekly progress reports',
        'Community access',
      ],
      badge: '🚀',
      color: 'blue',
    },
    {
      id: 'base',
      name: 'Base',
      price: '$9.99/mo',
      description: 'Everything you need to reach your goals',
      features: [
        'Full workout tracking',
        'Complete exercise library',
        'Daily progress insights',
        'Personalized recommendations',
        'Priority support',
      ],
      badge: '⭐',
      color: 'indigo',
      popular: true,
    },
    {
      id: 'prime',
      name: 'Prime',
      price: '$19.99/mo',
      description: 'Ultimate fitness experience',
      features: [
        'Everything in Base',
        'AI-powered workout plans',
        'Advanced analytics',
        'Nutrition planning',
        '1-on-1 coaching sessions',
        'Early access to new features',
      ],
      badge: '👑',
      color: 'purple',
    },
  ];

  const handleContinue = () => {
    if (selectedTier) {
      setProfileDraft({ feature_tier: selectedTier });
      markStepComplete(stepId);
      nextStep();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex flex-col items-center justify-center px-4 py-12">
      <div className="max-w-6xl w-full">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-white mb-3">Choose Your Plan</h1>
          <p className="text-slate-300 text-lg">
            Select the perfect tier to match your fitness journey
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {tiers.map((tier) => (
            <button
              key={tier.id}
              type="button"
              onClick={() => setSelectedTier(tier.id)}
              className={[
                'relative bg-white rounded-2xl p-8 border-3 transition-all transform hover:scale-105',
                'text-left cursor-pointer',
                selectedTier === tier.id
                  ? `border-${tier.color}-500 shadow-xl ring-4 ring-${tier.color}-100`
                  : 'border-gray-200 hover:border-gray-300 shadow-md',
              ].join(' ')}
            >
              {tier.popular && (
                <div className="absolute top-0 right-8 transform -translate-y-1/2">
                  <span className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white text-xs font-bold px-4 py-1 rounded-full">
                    MOST POPULAR
                  </span>
                </div>
              )}

              <div className="text-5xl mb-4">{tier.badge}</div>

              <h3 className="text-2xl font-bold text-gray-900 mb-2">{tier.name}</h3>
              <div className={`text-3xl font-bold text-${tier.color}-600 mb-3`}>{tier.price}</div>
              <p className="text-gray-600 text-sm mb-6">{tier.description}</p>

              <ul className="space-y-3 mb-6">
                {tier.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start text-sm text-gray-700">
                    <svg
                      className={`w-5 h-5 text-${tier.color}-500 mr-2 flex-shrink-0 mt-0.5`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    {feature}
                  </li>
                ))}
              </ul>

              {selectedTier === tier.id && (
                <div className={`text-center py-3 bg-${tier.color}-50 rounded-lg border-2 border-${tier.color}-200`}>
                  <span className={`text-${tier.color}-700 font-semibold text-sm`}>
                    ✓ Selected
                  </span>
                </div>
              )}
            </button>
          ))}
        </div>

        <div className="flex justify-between items-center max-w-2xl mx-auto">
          <button
            type="button"
            onClick={prevStep}
            className="text-slate-400 hover:text-slate-200 font-medium underline"
          >
            ← Back
          </button>

          <button
            type="button"
            onClick={handleContinue}
            disabled={!selectedTier}
            className={[
              'px-8 py-3 rounded-xl font-semibold transition-all',
              selectedTier
                ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 shadow-lg'
                : 'bg-slate-700 text-slate-500 cursor-not-allowed',
            ].join(' ')}
          >
            Continue to Next Step
          </button>
        </div>

        <p className="text-center text-sm text-slate-400 mt-6">
          You can change your plan anytime from your account settings
        </p>
      </div>
    </div>
  );
}
